import {
  HttpClient,
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { inject, Injectable, Injector } from '@angular/core';
import { toast } from 'ngx-sonner';
import { ActivatedRouteSnapshot, Router } from '@angular/router';
import { AuthGuard } from 'src/app/auth/guards/auth.guard';
import { LoadingService } from '@core/services/loading.service';
import { catchError, finalize, Observable, of, throwError } from 'rxjs';
import { WhoAmIService } from 'src/app/auth/services/whoami.service';
import { DataService } from 'src/app/data/services/data.service';
import { environment } from 'src/environments/environment';
import { ErrorDialogComponent } from '../components/error-dialog/error-dialog.component';
import { REDIRECT_REQUEST, SKIP_GLOBAL_ERROR_HANDLER } from './http-context-tokens';
import { HlmDialogService } from 'src/app/ui/dialog/src/lib/hlm-dialog.service';

@Injectable({
  providedIn: 'root',
})
export class GlobalRequestErrorHandlerInterceptor implements HttpInterceptor {
  private loadingService = inject(LoadingService);
  private router = inject(Router);
  private httpClient = inject(HttpClient);
  private whoami = inject(WhoAmIService);
  private dialog = inject(HlmDialogService);
  /** One unexpected-error dialog at a time: a page that fires several requests must not stack one per failure. */
  private errorDialogOpen = false;
  /** DataService is read lazily: it calls the API through HttpClient, which runs this interceptor. */
  private injector = inject(Injector);
  /** When /me was last reloaded after a 403, so a page failing on ten requests reloads it once. */
  private lastMeReload = 0;

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        if (!req.context.get(SKIP_GLOBAL_ERROR_HANDLER)) {
          this.loadingService.done();
          this.errorHandler(error, req);
          throw error;
        } else {
          // Only log errors for non-logout requests to avoid prerendering issues
          if (!req.url.includes('/logout')) {
            console.error('[Global Error Handler] ', error);
          }
          this.loadingService.done();
          // A page that handles its own errors still cannot recover from an expired account session:
          // without this, it shows its generic load error and the reader learns nothing
          if (error.status === 401 && this.needsSession(req.url)) {
            this.expireSession(req);
          }
          if (error.status === 403) {
            this.reloadMeOnForbidden(req.url);
          }
          return throwError(() => error);
        }
      })
    );
  }

  /** Whether the page shown, or any route above it, is guarded by AuthGuard. */
  private needsAccount(route: ActivatedRouteSnapshot | null): boolean {
    for (let r = route; r; r = r.firstChild) {
      if (r.routeConfig?.canActivate?.includes(AuthGuard)) {
        return true;
      }
    }
    return false;
  }

  /**
   * Whether the URL is an admin API endpoint that answers only with the account session, so a 401
   * from it means the session expired. Requests to a service's own node are left out: there a 401
   * is about the credentials the page sent, and the page reports it.
   */
  private needsSession(url: string): boolean {
    return url.startsWith(`${environment.RH_BASE_URL}/srvs-mgmt/`)
      || url.startsWith(`${environment.RH_BASE_URL}/plugins-mgmt/`);
  }

  /**
   * A 403 from the admin may mean the account no longer belongs to the organization the console
   * works in, as when its owner removed it: /me is read again, and the pages follow what it says,
   * the list of organizations when none is left. At most once every ten seconds.
   */
  private reloadMeOnForbidden(url: string) {
    if (!url.startsWith(environment.RH_BASE_URL) || !this.whoami.isAuthenticated() || Date.now() - this.lastMeReload < 10_000) {
      return;
    }
    this.lastMeReload = Date.now();
    this.injector.get(DataService).refresh();
  }

  /** Clears the expired account session and, on a page that needs an account, goes to sign in. */
  private expireSession(req: HttpRequest<any>) {
    // On a page that needs no account, like /pricing or the home page, an expired session is
    // forgotten quietly and the reader stays where they are: the page works without one
    const onPublicPage = !this.needsAccount(this.router.routerState.snapshot.root);
    if (!onPublicPage && this.router.url !== '/auth/login' && !this.router.url.startsWith('/checkout')) {
      // One toast when several requests of the same page fail together
      toast.error('Your session expired. Sign in again.', { id: 'session-expired' });
    }
    // Call logout endpoint to clear server-side cookie
    const logoutUrl = `${environment.RH_BASE_URL}/logout`;
    this.httpClient
      .post(logoutUrl, null, {
        withCredentials: true,
        context: req.context.set(SKIP_GLOBAL_ERROR_HANDLER, true),
      })
      .pipe(
        catchError(() => of(null)),
        finalize(() => (onPublicPage ? this.whoami.clear() : this.whoami.clearAndGoToSignin()))
      )
      .subscribe();
  }

  // note: we cannot use TranslateService due to circular dependency
  private errorHandler(error: HttpErrorResponse, req: HttpRequest<any>) {
    this.loadingService.done();
    switch (error.status) {
      case 401:
        // only the admin answers with the account session: a 401 from a service's own node is about
        // the token sent there, which ServiceTokenRetryInterceptor has already renewed once
        if (req.url.startsWith(environment.RH_BASE_URL)) {
          this.expireSession(req);
        } else {
          toast.error('The service did not accept the request. Reload the page.', { id: 'service-unauthorized' });
        }
        break;
      case 403: // FORBIDDEN
        toast.error('You do not have permission to do this.');
        this.reloadMeOnForbidden(req.url);
        break;
      case 404: // NOT FOUND - DO NOT REMOVE!!!
        console.log('NOT FOUND MUST be handled by subsciption');
        if (req.context.get(REDIRECT_REQUEST)) {
          this.router.navigate(['not-found']);
        }
        break;
      case 409:
        toast.error('This already exists.');
        break;
      case 417:
        // TODO: check if url is mgmt/users/
        return;
      case 429:
        toast.error('We see unusual activity on your account. Contact support.');
        break;
      case 0: // the request never reached a server: offline, DNS, CORS, or the API is down
        toast.error('Connection lost. Check your network, then try again.', { id: 'server-unreachable' });
        break;
      case 502:
      case 503:
      case 504: // the API is down or restarting: not a bug, so no dialog and no stack trace
        toast.error('Ulabase is not reachable right now. Try again in a minute.', { id: 'server-unreachable' });
        break;
      default:
        if (error && !this.errorDialogOpen) {
          this.errorDialogOpen = true;
          this.dialog.open(ErrorDialogComponent, { context: { error } })
            .closed$.subscribe(() => (this.errorDialogOpen = false));
        }
        break;
    }
  }
}
